import { useEffect, useRef, useState } from 'react';
import { api, audioUrl, getToken } from '../api/client.js';
import { PauseIcon, PlayIcon, SpeakerIcon } from './icons.jsx';

/**
 * Reproductor de audio per-pagina con encadenado al cambiar de pagina.
 *
 * Props:
 *  - story          : objeto del cuento. Espera story.audio_available={cover, scenes:[]}.
 *  - part           : pagina actual ('cover' | {sceneIndex:N}).
 *  - autoplay       : si true, al cambiar de pagina arranca a reproducir esta
 *                     pagina (generandola primero si hace falta). El padre
 *                     decide cuando true/false segun los callbacks abajo.
 *  - onUserPlay     : llamado cuando el usuario INICIA reproduccion (click play).
 *  - onUserPause    : llamado cuando el usuario PAUSA con el boton.
 *  - onCostUpdated  : (breakdown, lineaNueva) tras generar un segmento.
 *
 * El padre tipicamente:
 *    autoplayMode=false al inicio
 *    onUserPlay  -> autoplayMode=true
 *    onUserPause -> autoplayMode=false
 *  De este modo, si al cambiar de pagina autoplayMode sigue true, el nuevo
 *  segmento se reproduce automaticamente sin click.
 */
export default function AudioPlayer({
  story,
  part,
  autoplay = false,
  onUserPlay,
  onUserPause,
  onCostUpdated,
}) {
  const audioRef = useRef(null);
  // Mantenemos el autoplay actual en un ref para que el effect de cambio de
  // pagina lea el valor "vivo" sin tener que dependerse de el (lo que haria
  // que toggles del padre causen un reset indebido del audio en curso).
  const autoplayRef = useRef(autoplay);
  useEffect(() => {
    autoplayRef.current = autoplay;
  }, [autoplay]);

  const [available, setAvailable] = useState(() => isAvailable(story, part));
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const src = audioUrl(story.slug, part);
  const label = labelFor(part);

  // Al cambiar de pagina (o de cuento): pausamos, reseteamos y, si el padre
  // indica que se estaba escuchando, encadenamos la siguiente pagina.
  useEffect(() => {
    const el = audioRef.current;
    if (el) {
      el.pause();
      el.currentTime = 0;
    }
    setPlaying(false);
    setError(null);
    const nowAvailable = isAvailable(story, part);
    setAvailable(nowAvailable);

    if (!autoplayRef.current) return;

    let cancelled = false;
    (async () => {
      try {
        if (!nowAvailable) {
          try {
            await ensureGenerated(false);
          } catch {
            // Si falla la generacion, dejamos el boton de play visible para retry.
            return;
          }
        }
        if (cancelled) return;
        const el2 = audioRef.current;
        if (!el2) return;
        // Forzamos recarga: el src apunta al nuevo segmento.
        el2.load();
        try {
          await el2.play();
        } catch {
          // Politicas de autoplay del navegador o interrupcion: silenciamos.
          // El usuario seguira viendo el boton para arrancar manualmente.
        }
      } catch {
        // Ignorado: errores ya reflejados en setError dentro de ensureGenerated.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story?.slug, partKey(part)]);

  async function ensureGenerated(showBusy = true) {
    const token = getToken();
    if (!token) {
      const err = new Error('Para generar el audio por primera vez necesitas la contraseña.');
      err.code = 'no-token';
      setError(err.message);
      throw err;
    }
    if (showBusy) setBusy(true);
    setError(null);
    try {
      const res = await api.stories.generateAudio(story.slug, part);
      setAvailable(true);
      if (audioRef.current) audioRef.current.load();
      if (onCostUpdated && res?.breakdown) onCostUpdated(res.breakdown, res.cost);
    } catch (err) {
      setError(err.message ?? 'No se pudo generar el audio.');
      throw err;
    } finally {
      if (showBusy) setBusy(false);
    }
  }

  async function togglePlay() {
    setError(null);
    if (!available) {
      try {
        await ensureGenerated(true);
      } catch {
        return;
      }
    }
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      try {
        await el.play();
        onUserPlay?.();
      } catch {
        setError('No se pudo reproducir el audio.');
      }
    } else {
      el.pause();
      onUserPause?.();
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 rounded-full bg-brand-50 px-2 py-1">
        {available ? (
          <button
            type="button"
            onClick={togglePlay}
            aria-label={playing ? `Pausar ${label}` : `Reproducir ${label}`}
            className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-600 text-white hover:bg-brand-700"
          >
            {playing ? <PauseIcon size="0.85em" /> : <PlayIcon size="0.85em" />}
          </button>
        ) : (
          <button
            type="button"
            onClick={togglePlay}
            disabled={busy}
            aria-label={`Generar y escuchar ${label}`}
            className="flex h-7 items-center gap-1 rounded-full bg-brand-600 px-2 text-white hover:bg-brand-700 disabled:opacity-60"
          >
            <SpeakerIcon size="0.85em" />
          </button>
        )}
        <span className="text-xs font-semibold text-brand-700">
          {busy ? 'Generando...' : 'Escuchar'}
        </span>
        <audio
          ref={audioRef}
          src={src ?? undefined}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          onError={() => {
            // Solo mostrar error si tenia que estar disponible.
            if (available) setError('Error reproduciendo audio.');
          }}
          preload="none"
        />
      </div>
      {error && (
        <p className="max-w-xs text-[10px] leading-tight text-red-600">{error}</p>
      )}
    </div>
  );
}

function partKey(part) {
  if (part === 'cover') return 'cover';
  if (part && typeof part.sceneIndex === 'number') return `scene:${part.sceneIndex}`;
  return '';
}

function labelFor(part) {
  if (part === 'cover') return 'la portada';
  if (part && typeof part.sceneIndex === 'number') return `la escena ${part.sceneIndex + 1}`;
  return 'esta pagina';
}

function isAvailable(story, part) {
  const avail = story?.audio_available;
  if (!avail) return false;
  if (part === 'cover') return !!avail.cover;
  if (part && typeof part.sceneIndex === 'number') {
    return !!avail.scenes?.[part.sceneIndex];
  }
  return false;
}
